import { useState, useCallback, useEffect, useRef } from 'react';
import clsx from 'clsx';
import { useRunQcTable } from '../../../hooks/useRunQcTable';
import type { AISuggestion as ImportedAISuggestion, RunQcTableResponse } from '../../../hooks/useRunQcTable';
import { AISuggestionReview } from '../shared/AISuggestionReview';
import styles from './TableSection.module.css';

type HeaderRunRegistration = {
  label: string;
  run: () => Promise<void>;
  disabled?: boolean;
};

export type SectionState = 'view' | 'edit' | 'running' | 'suggestions' | 'applied';
export type TableName = 'contacts' | 'deadlines' | 'requirements' | 'fees_prizes' | 'media' | 'categories' | 'judging' | 'program_details' | 'vetting';
export type AISuggestion = ImportedAISuggestion;
export type AISuggestionsResponse = RunQcTableResponse;

export interface TableSectionProps {
  title: string;
  description?: string;
  tableName: TableName;
  programId: string;
  baciCode: string;
  seedUrl: string;
  organization?: string;
  editMode: boolean;
  children: React.ReactNode;
  onSaveComplete?: () => void;
  onApplySuggestions?: (suggestions: Record<string, string | null>) => void;
  className?: string;
  runLabel?: string;
  runDisabled?: boolean;
  onRegisterRun?: (config: HeaderRunRegistration | null) => void;
}

export function TableSection({
  title,
  description,
  tableName,
  programId,
  baciCode,
  seedUrl,
  organization,
  editMode,
  children,
  onSaveComplete: _onSaveComplete,
  onApplySuggestions,
  className = '',
  runLabel,
  runDisabled = false,
  onRegisterRun
}: TableSectionProps) {
  const [state, setState] = useState<SectionState>('view');
  const [aiSuggestions, setAISuggestions] = useState<AISuggestionsResponse | null>(null);
  const [acceptedFields, setAcceptedFields] = useState<Record<string, boolean>>({});
  const [reportUrl, setReportUrl] = useState<string | null>(null);

  const runQcMutation = useRunQcTable(tableName, {
    onSuccess: (data) => {
      setAISuggestions(data);
      setReportUrl(data.report_path || null);
      const allAccepted: Record<string, boolean> = {};
      data.suggestions?.forEach((sugg) => {
        allAccepted[sugg.field] = true;
      });
      setAcceptedFields(allAccepted);
      setState('suggestions');
    },
    onError: (error) => {
      console.error('BAM_check failed:', error);
      alert(`AI research failed: ${error.message}`);
      setState('edit');
    }
  });

  const lastRegistrationRef = useRef<string>('');

  const performRun = useCallback(async () => {
    setState('running');
    setReportUrl(null);
    setAISuggestions(null);

    await runQcMutation.mutateAsync({
      program_id: programId,
      baci_code: baciCode,
      seed_url: seedUrl,
      organization
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runQcMutation.mutateAsync, programId, baciCode, seedUrl, organization]);

  useEffect(() => {
    if (!runLabel || !onRegisterRun) {
      if (lastRegistrationRef.current !== '') {
        onRegisterRun?.(null);
        lastRegistrationRef.current = '';
      }
      return;
    }

    const isDisabled = runDisabled || runQcMutation.isPending || !editMode;
    const registrationKey = `${runLabel}-${isDisabled}`;

    if (lastRegistrationRef.current === registrationKey) {
      return;
    }

    const registration: HeaderRunRegistration = {
      label: runLabel,
      run: performRun,
      disabled: isDisabled
    };

    onRegisterRun(registration);
    lastRegistrationRef.current = registrationKey;

    return () => {
      onRegisterRun(null);
      lastRegistrationRef.current = '';
    };
  }, [runLabel, onRegisterRun, performRun, runDisabled, runQcMutation.isPending, editMode]);

  const handleAcceptAll = () => {
    const allAccepted: Record<string, boolean> = {};
    aiSuggestions?.suggestions?.forEach((sugg) => {
      allAccepted[sugg.field] = true;
    });
    setAcceptedFields(allAccepted);
  };

  const handleRejectAll = () => {
    setAcceptedFields({});
    setState('edit');
    setAISuggestions(null);
  };

  const handleAcceptSelected = () => {
    if (!aiSuggestions?.suggestions) {
      setState('edit');
      return;
    }

    const acceptedChanges: Record<string, string | null> = {};
    aiSuggestions.suggestions.forEach((sugg) => {
      if (acceptedFields[sugg.field]) {
        acceptedChanges[sugg.field] = sugg.suggestedValue;
      }
    });

    if (Object.keys(acceptedChanges).length > 0) {
      onApplySuggestions?.(acceptedChanges);
    }

    setState('applied');
  };

  const effectiveState = editMode ? state : 'view';
  const shouldRenderLocalRunButton = Boolean(runLabel && !onRegisterRun);

  return (
    <section className={clsx(styles.section, className)}>
      <div className={styles.header}>
        <div>
          <p className={styles.title}>{title}</p>
          {description && <p className={styles.description}>{description}</p>}
        </div>
        {shouldRenderLocalRunButton && (
          <div className={styles.actions}>
            <button
              type="button"
              onClick={() => void performRun()}
              disabled={runDisabled || runQcMutation.isPending || !editMode}
              className={styles.runButton}
            >
              {runQcMutation.isPending ? 'Running…' : runLabel}
            </button>
          </div>
        )}
      </div>

      <div className={styles.body}>{children}</div>

      {effectiveState === 'running' && (
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <span>Gathering updated references…</span>
        </div>
      )}

      {effectiveState === 'suggestions' && aiSuggestions && (
        <div className={styles.suggestions}>
          <p className={styles.suggestionsHeader}>Review suggested updates</p>
          <div className={styles.suggestionList}>
            {aiSuggestions.suggestions?.map((sugg) => (
              <AISuggestionReview
                key={sugg.field}
                fieldLabel={sugg.field}
                currentValue={sugg.currentValue}
                suggestedValue={sugg.suggestedValue}
                source={sugg.source}
                accepted={acceptedFields[sugg.field] || false}
                onToggleAccept={(accepted) => setAcceptedFields((prev) => ({ ...prev, [sugg.field]: accepted }))}
              />
            ))}
          </div>
          {reportUrl && (
            <a
              href={reportUrl.startsWith('http') ? reportUrl : `/${reportUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.reportLink}
            >
              View research transcript
            </a>
          )}
          <div className={styles.suggestionsActions}>
            <button type="button" onClick={handleRejectAll} className={styles.rejectButton}>
              Reject all
            </button>
            <button
              type="button"
              onClick={handleAcceptSelected}
              className={styles.acceptButton}
              disabled={Object.values(acceptedFields).filter(Boolean).length === 0}
            >
              Accept selected ({Object.values(acceptedFields).filter(Boolean).length})
            </button>
            <button type="button" onClick={handleAcceptAll} className={styles.acceptAllButton}>
              Accept all
            </button>
          </div>
        </div>
      )}

      {effectiveState === 'applied' && (
        <div className={styles.appliedState}>
          {children}
          <span className={styles.appliedBanner}>✓ Changes applied. Save to update the database.</span>
        </div>
      )}
    </section>
  );
}

export default TableSection;
